import React from 'react';
import { createPortal } from 'react-dom';
import { Typography, Box, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import { getBackgroundColor } from '../themes';

const ViewerContainer = styled(Box)(({ theme }) => ({
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: getBackgroundColor(theme, 'paper'),
    '& iframe': {
        border: 'none',
        width: '100%',
        height: '100%',
        backgroundColor: getBackgroundColor(theme, 'paper')
    }
}));

const ViewerToolbar = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: getBackgroundColor(theme, 'preview'),
    minHeight: '48px'
}));

const PreviewModalContainer = styled(Box)(({ theme }) => ({
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: theme.palette.background.modal,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1400,
    padding: '2%'
}));

const ModalContent = styled(Box, {
    shouldForwardProp: (prop) => !['isImage', 'isPdf', 'isText'].includes(prop)
})(({ theme, isImage, isPdf, isText }) => ({
    backgroundColor: getBackgroundColor(theme, 'paper'),
    padding: isImage ? 0 : '2%',
    borderRadius: theme.shape.borderRadius,
    maxWidth: isPdf ? '95%' : isText ? '80%' : isImage ? '90%' : '60%',
    width: isPdf || isText ? '95%' : 'auto',
    height: isPdf || isText ? '90vh' : 'auto',
    maxHeight: '90vh',
    overflow: 'auto',
    position: 'relative',
    ...(isPdf && {
        display: 'flex',
        flexDirection: 'column'
    }),
    ...(isText && {
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: getBackgroundColor(theme, 'paper'),
    })
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
}));

const PreviewModal = ({ attachment, onClose }) => {
    const isImage = attachment.type?.startsWith('image/') ||
        attachment.mimetype?.startsWith('image/');
    const isPdf = attachment.type === 'application/pdf' ||
        attachment.mimetype === 'application/pdf';
    const isText = attachment.type === 'text/plain' ||
        attachment.mimetype === 'text/plain';

    const renderTextViewer = () => {
        // Create a blob URL for the text content
        const blob = new Blob([attachment.content], { type: 'text/plain' });
        const textUrl = URL.createObjectURL(blob);

        return (
            <ViewerContainer>
                <ViewerToolbar>
                    <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
                        {attachment.originalName}
                    </Typography>
                </ViewerToolbar>
                <iframe
                    src={textUrl}
                    title={attachment.originalName}
                    onLoad={() => {
                        // Clean up the blob URL after the iframe loads
                        URL.revokeObjectURL(textUrl);
                    }}
                />
            </ViewerContainer>
        );
    };

    return createPortal(
        <PreviewModalContainer onClick={onClose}>
            <ModalContent
                isImage={isImage}
                isPdf={isPdf}
                isText={isText}
                onClick={(e) => e.stopPropagation()}
            >
                <CloseButton onClick={onClose}>
                    <CloseIcon />
                </CloseButton>

                {isText ? (
                    renderTextViewer()
                ) : isImage ? (
                    <img
                        src={attachment.preview || attachment.base64}
                        alt={attachment.originalName || 'Preview'}
                        style={{
                            maxWidth: '100%',
                            maxHeight: '90vh',
                            objectFit: 'contain',
                        }}
                    />
                ) : isPdf ? (
                    <>
                        <Box sx={(theme) => ({
                            display: 'flex',
                            alignItems: 'center',
                            padding: '12px 16px',
                            borderBottom: '1px solid #0000001a',
                            backgroundColor: getBackgroundColor(theme, 'preview')
                        })}>
                            <svg className="attachment-icon" viewBox="0 0 24 24" width="20" height="20">
                                <path fill="#666" d="M19,3A2,2 0 0,1 21,5V19A2,2 0 0,1 19,21H5C3.89,21 3,20.1 3,19V5C3,3.89 3.89,3 5,3H19M10.59,10.08C10.57,10.13 10.3,11.84 8.5,14.77C8.5,14.77 5,16.58 5.83,17.94C6.5,19 8.15,17.9 9.56,15.27C9.56,15.27 11.38,14.63 13.79,14.45C13.79,14.45 17.65,16.19 18.17,14.34C18.69,12.5 15.12,12.9 14.5,13.09C14.5,13.09 12.46,11.75 12,9.89C12,9.89 13.13,5.95 11.38,6C9.63,6.05 10.29,9.12 10.59,10.08Z" />
                            </svg>
                            <span style={{ marginLeft: '8px', fontWeight: 'medium', color: '#333' }}>
                                {attachment.originalName || 'PDF Document'}
                            </span>
                        </Box>
                        <iframe
                            src={attachment.preview || attachment.base64}
                            title={attachment.originalName || 'PDF document'}
                            width="100%"
                            height="100%"
                            style={{
                                border: 'none',
                                flex: 1
                            }}
                        />
                    </>
                ) : (
                    <>
                        <Typography
                            variant="h6"
                            sx={{
                                p: 2,
                                borderBottom: 1,
                                borderColor: 'divider',
                                color: theme => theme.palette.text.primary
                            }}
                        >
                            {attachment.originalName || 'Document'}
                        </Typography>
                        <Typography
                            component="pre"
                            sx={{
                                whiteSpace: 'pre-wrap',
                                fontFamily: 'monospace',
                                fontSize: '0.875rem',
                                mt: 2,
                            }}
                        >
                            {attachment.content}
                        </Typography>
                    </>
                )}
            </ModalContent>
        </PreviewModalContainer>,
        document.body
    );
};

export default PreviewModal;